import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import '@chancenwerk/client/design/components/cr-textarea.js';
import { MarktAkquiseContactRecord } from '@chancenwerk/markt/entities/MarktAkquiseContactRecord.js';
import { MarktAkquiseStatus } from '@chancenwerk/markt/entities/MarktAkquiseStatus.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DateTime } from 'luxon';

@customElement('markt-dialog-contact-record-edit')
export class MarktDialogContactRecordEdit extends ChromiumDialog<Maybe<MarktAkquiseContactRecord>> {
  static override get element_name() {
    return 'markt-dialog-contact-record-edit';
  }

  override heading?: string | undefined = 'Kontakt bearbeiten';

  @property({ attribute: false }) record = new RXController(this, new MarktAkquiseContactRecord({}));

  override content() {
    return html`
      <cr-flex direction="column" space="md" ${css({ width: '700px' })}>
        <chancen-component-card-title>Details</chancen-component-card-title>
        <cr-flex horizontal space="md">
          <x-entity-picker
            flex
            .value=${this.record.value.fromID}
            placeholder="von"
            clearable
            .options=${{
              class: ChancenwerkUser,
              selectionSet: { vorname: 1, nachname: 1 },
              operation: UserAPI.subscribeByFilter,
              filter: new ChancenwerkUserFilter({
                userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
              }),
            }}
            @value-picked=${(e: ValueEvent<Maybe<string>>) => {
              this.record.value.fromID = e.detail.value;
            }}
          ></x-entity-picker>

          <x-entity-picker
            flex
            .value=${this.record.value.fromID}
            placeholder="an"
            clearable
            .options=${{
              class: ChancenwerkUser,
              selectionSet: { vorname: 1, nachname: 1 },
              operation: UserAPI.subscribeByFilter,
              filter: new ChancenwerkUserFilter({
                userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
              }),
            }}
            @value-picked=${(e: ValueEvent<Maybe<string>>) => {
              this.record.value.toID = e.detail.value;
            }}
          ></x-entity-picker>

          <cr-date-picker-dropdown
            flex
            placeholder="wann"
            .end-year=${DateTime.now().plus({ years: 10 }).year}
            click-to-save
            clearable
            .value=${this.record.value.when}
            @value-changed=${(e: ValueEvent<Maybe<DateTime>>) => {
              this.record.value.when = e.detail.value;
            }}
          ></cr-date-picker-dropdown>
        </cr-flex>

        <cr-textarea
          placeholder="Rückmeldung"
          .value=${this.record.value.response ?? ''}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            this.record.value.response = e.detail.value;
          }}
        ></cr-textarea>

        <br />

        <chancen-component-card-title>nächste Schritte</chancen-component-card-title>

        <cr-textarea
          placeholder="was"
          .value=${this.record.value.nextStep ?? ''}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            this.record.value.nextStep = e.detail.value;
          }}
        ></cr-textarea>

        <cr-date-picker-dropdown
          flex
          placeholder="wann"
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          click-to-save
          clearable
          .value=${this.record.value.nextStepWhen}
          @value-changed=${(e: ValueEvent<Maybe<DateTime>>) => {
            this.record.value.nextStepWhen = e.detail.value;
          }}
        ></cr-date-picker-dropdown>

        <br />

        <chancen-component-card-title>Status</chancen-component-card-title>

        <cr-dropdown-selection
          .selectables=${Object.keys(MarktAkquiseStatus)}
          .renderString=${(status: MarktAkquiseStatus) => MarktAkquiseStatus[status]}
          .value=${this.record.value.status}
          @value-picked=${(e: ValueEvent<Maybe<MarktAkquiseStatus>>) => {
            this.record.value.status = e.detail.value;
          }}
        >
        </cr-dropdown-selection>

        <cr-flex justify="end" space="sm">
          <cr-button
            @click=${async () => {
              try {
                await this.close(this.record.value);
              } catch (e: any) {
                ChromiumSnackbar.show(e.message);
              }
            }}
          >
            Speichern
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }
}
